import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  fetchPostComments,
  createComment,
  updateComment,
  fetchCommentReplies,
  toggleCommentLikeAsync,
  hydrateRepliesFromCache,
  setCommentDisplayCount,
  patchCommentLikeCount,
} from "../../../../../redux/slices/posts/commentSlice.js";
import { selectCommentPaginationData } from "../../../../../redux/slices/postSlice.js";
import {
  fetchSavedComments,
  saveCommentToLocal,
  unsaveCommentFromLocal,
} from "../../../../../redux/slices/savedCommentsSlice.js";
import { openDeleteConfirm } from "../../../../../redux/slices/uiSlice.js";
import { auth } from "../../../../../firebaseConfig";
import { getDisplayName } from "../../../../../utils/userDisplay";
import {
  useNotificationTriggers,
  parseMentions,
} from "../../../../../features/notifications";
import {
  hasMentions,
  convertLegacyMentions,
  getMentionedUserIds,
  validateStructuredMentions,
} from "../../../../../features/notifications/utils/mentionParser";
import { injectStructuredMentions } from "../../../../../utils/mentionInjection.js";
import useContentOwnership from "../../../../../hooks/useContentOwnership.js";

const INITIAL_DISPLAY_COUNT = 8;
const COMMENT_MAX_LENGTH = 2000;

export const useCommentThread = ({ postId, isVisible }) => {
  const dispatch = useDispatch();

  // Global State
  const currentUser = useSelector((state) => state.auth.currentUser);
  const userData = useSelector((state) => state.auth.userData);
  const comments = useSelector((state) => state.comments.items[postId] || []);
  const commentLikePending = useSelector(
    (state) => state.commentLikes?.commentLikePending || {},
  );
  const likedCommentIds = useSelector(
    (state) => state.commentLikes?.likedCommentIds || {},
  );
  const commentPagination = useSelector(
    (state) =>
      state.comments?.pagination?.[postId] || {
        hasMore: false,
        nextCursor: null,
      },
  );
  const postForNotify = useSelector((state) =>
    (state.posts.posts || []).find((p) => p.id === postId),
  );
  const { totalComments, remainingComments, hasMoreOnServer, displayCount } =
    useSelector((state) => selectCommentPaginationData(state, postId));

  const deleteConfirmState = useSelector((state) => state.ui.showDeleteConfirm);
  const savedCommentsState = useSelector(
    (state) => state.savedComments.items || [],
  );
  const savedCommentsLoading = useSelector(
    (state) => state.savedComments.loading,
  );
  const savedCommentsHasFetched = useSelector(
    (state) => state.savedComments.hasFetched,
  );

  // Local State
  const [isLoading, setIsLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [isEditing, setIsEditing] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [showOptions, setShowOptions] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showReplyInput, setShowReplyInput] = useState(null);
  const [replyToUser, setReplyToUser] = useState(null);
  const [mentionMap, setMentionMap] = useState({});

  // Refs
  const isLoadingRef = useRef(false);
  const commentInputRef = useRef(null);
  const optionsMenuRef = useRef(null);
  const commentSectionRef = useRef(null);
  const replyFetchTimestamps = useRef(new Map());

  // Ownership hook
  const { canManage: isCommentOwner } = useContentOwnership();
  const { triggerCommentNotification, triggerMentionNotification } =
    useNotificationTriggers();

  const savedSet = useMemo(
    () => new Set(savedCommentsState.map((c) => c.id)),
    [savedCommentsState],
  );

  // Load Comments
  const loadComments = useCallback(
    async (loadMore = false) => {
      if (process.env.NODE_ENV !== "production") {
        console.log(
          `[CommentBox] loadComments called for postId: ${postId}, loadMore: ${loadMore}`,
        );
      }
      if (isLoadingRef.current && !loadMore) return;

      isLoadingRef.current = true;
      setIsLoading(true);
      try {
        const result = await dispatch(
          fetchPostComments({
            postId,
            limit: 5,
            cursor: loadMore ? commentPagination.nextCursor : null,
          }),
        ).unwrap();
        if (process.env.NODE_ENV !== "production") {
          console.log(
            `[CommentBox] fetchPostComments fulfilled for postId: ${postId}, comments fetched: ${result.comments?.length ?? 0}, skipped: ${result.skipped}`,
          );
        }
      } catch (error) {
        console.error("Failed to load comments:", error);
        toast.error("Failed to load comments");
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
        if (process.env.NODE_ENV !== "production") {
          console.log(
            `[CommentBox] isLoading set to false for postId: ${postId}`,
          );
        }
      }
    },
    [postId, commentPagination.nextCursor, dispatch],
  );

  // Initial Fetch & Cache Hydration
  useEffect(() => {
    const fetchTimestamps = replyFetchTimestamps.current;
    if (postId && isVisible) {
      loadComments();
    }
    return () => {
      fetchTimestamps.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, isVisible, dispatch]);

  // Saved Comments Setup
  useEffect(() => {
    if (currentUser?.uid && !savedCommentsLoading && !savedCommentsHasFetched) {
      dispatch(fetchSavedComments());
    }
  }, [currentUser?.uid, savedCommentsLoading, savedCommentsHasFetched, dispatch]);

  // Options Menu Click-Outside
  useEffect(() => {
    if (!showOptions) return;
    const handleClickOutside = (e) => {
      if (
        optionsMenuRef.current &&
        !optionsMenuRef.current.contains(e.target)
      ) {
        setShowOptions(null);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setShowOptions(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showOptions]);

  // Derived Values
  const sortedComments = useMemo(() => {
    if (!Array.isArray(comments)) return [];
    const list = [...comments];
    switch (sortOrder) {
      case "oldest":
        return list.reverse();
      case "top":
        return list.sort((a, b) => (b.likes || 0) - (a.likes || 0));
      case "newest":
      default:
        return list;
    }
  }, [comments, sortOrder]);

  const visibleComments = useMemo(
    () => sortedComments.filter((comment) => !comment?.deletedAt),
    [sortedComments],
  );

  const displayedComments = useMemo(
    () => visibleComments.slice(0, displayCount),
    [visibleComments, displayCount],
  );

  // Handlers
  const handleMentionSelect = useCallback((user) => {
    if (!user || !user.username || !user.id) return;
    setMentionMap((prev) => ({
      ...prev,
      [user.username]: user.id,
    }));
  }, []);

  const handleLoadMoreComments = () => {
    loadComments(true);
    dispatch(setCommentDisplayCount({ postId, count: displayCount + 8 }));
    setIsExpanded(true);
  };

  const handleShowLessComments = () => {
    dispatch(setCommentDisplayCount({ postId, count: INITIAL_DISPLAY_COUNT }));
    setIsExpanded(false);
    if (commentSectionRef.current) {
      commentSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  const handleSortChange = (newOrder) => {
    setSortOrder(newOrder);
    dispatch(
      setCommentDisplayCount({
        postId,
        count: INITIAL_DISPLAY_COUNT,
      }),
    );
  };

  const handleToggleSaveComment = async (comment) => {
    if (!currentUser) {
      toast.error("Please log in to save comments");
      return;
    }
    const payload = {
      id: comment.id,
      postId,
      userId: comment.userId || null,
      username: comment.username,
      profession: comment.profession,
      photoURL: comment.photoURL,
      text: comment.text,
      timestamp: comment.timestamp,
      likes: comment.likes || 0,
      replyCount: comment.replyCount || 0,
    };
    const isSaved = savedSet.has(comment.id);
    try {
      if (isSaved) {
        await dispatch(unsaveCommentFromLocal(comment.id)).unwrap();
        toast.success("Comment unsaved");
      } else {
        await dispatch(saveCommentToLocal(payload)).unwrap();
        toast.success("Comment saved");
      }
    } catch (e) {
      toast.error(e?.message || "Failed to update saved comment");
    }
  };

  const handleCommentSubmit = async () => {
    const isDev = process.env.NODE_ENV !== "production";
    if (isDev) {
      console.group("Comment Submission");
      console.time("commentSubmissionDuration");
    }

    try {
      const trimmedText = commentText.trim();
      if (!trimmedText) {
        toast.error("Comment cannot be empty");
        return;
      }
      if (!postId) {
        toast.error("No post selected. Please try again.");
        return;
      }
      if (!auth?.currentUser) {
        toast.error(
          "Authentication is initializing. Please wait a moment and try again.",
        );
        return;
      }

      setIsSubmitting(true);

      let currentUserData;
      if (userData) {
        currentUserData = {
          userId: currentUser?.uid || auth?.currentUser?.uid || null,
          username: getDisplayName({
            profile: userData,
            authUser: auth?.currentUser,
            candidates: [
              userData?.username,
              userData?.displayName,
              userData?.name,
            ],
          }),
          profession: userData.userProfession || "",
          photoURL:
            userData.photoURL ||
            userData.userPhotoURL ||
            auth?.currentUser?.photoURL ||
            "",
        };
      } else {
        const authUser = auth.currentUser;
        const fallbackUsername = getDisplayName({ authUser });
        currentUserData = {
          userId: authUser?.uid || currentUser?.uid || null,
          username: fallbackUsername || "User",
          profession: "",
          photoURL: authUser?.photoURL || "",
        };
      }

      // Inject structured tokens using the local mentionMap
      const injectedText = injectStructuredMentions(trimmedText, mentionMap);

      const convertedText = await convertLegacyMentions(injectedText);
      const validatedMentions = validateStructuredMentions(convertedText);
      const safeText = validatedMentions.text.trim();

      if (!safeText) {
        toast.error("Comment cannot be empty");
        return;
      }

      if (validatedMentions.hadInvalidTokens) {
        toast.warn("Some malformed mentions were normalized before posting.");
      }

      const tempId = `temp-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;

      setCommentText("");
      setMentionMap({});
      if (commentInputRef?.current) {
        try {
          commentInputRef.current.style.height = "auto";
        } catch (err) {
          if (isDev) {
            console.error("[CommentBox] Error resetting textarea height:", err);
          }
        }
      }

      try {
        const result = await dispatch(
          createComment({
            postId,
            comment: {
              ...currentUserData,
              text: safeText,
            },
            tempId,
          }),
        ).unwrap();

        dispatch(setCommentDisplayCount({ postId, count: displayCount + 1 }));

        Promise.allSettled([
          (async () => {
            const authorId = postForNotify?.userId || null;
            if (
              authorId &&
              authorId !== (currentUser?.uid || auth?.currentUser?.uid)
            ) {
              await triggerCommentNotification(
                { id: postId, content: postForNotify?.content || "" },
                {
                  id: result?.comment?.id,
                  content: result?.comment?.text || safeText,
                },
                authorId,
              );
            }
          })(),
          (async () => {
            if (!hasMentions(safeText)) return;
            const mentionedUserIds = await parseMentions(safeText);
            const currentUserId = currentUser?.uid || auth?.currentUser?.uid;

            if (mentionedUserIds.length > 0) {
              await triggerMentionNotification(
                mentionedUserIds.filter((id) => id !== currentUserId),
                {
                  postId: postId,
                  commentId: result?.comment?.id,
                  type: "comment",
                  text: safeText.substring(0, 100),
                },
              );
            }
          })(),
        ]).catch((notificationError) => {
          console.warn(
            "Failed to send comment notifications:",
            notificationError,
          );
        });

      } catch (error) {
        if (isDev) {
          console.error("Comment submission error", {
            error,
            postId,
            username: currentUserData.username,
          });
        }
        setCommentText(safeText);
        const errorMessages = {
          "network-error": "Network issue. Please check your connection.",
          "permission-denied": "You are not authorized to comment.",
          default: "Failed to post comment. Please try again.",
        };
        const errorType = error.code || error?.payload?.code || "default";
        toast.error(errorMessages[errorType] || errorMessages["default"]);
      } finally {
        setIsSubmitting(false);
      }
    } catch (unexpectedError) {
      console.error("Unexpected error in comment submission", unexpectedError);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      if (isDev) {
        console.timeEnd("commentSubmissionDuration");
        console.groupEnd();
      }
    }
  };

  const handleCommentEdit = async (commentId) => {
    if (!editedText.trim()) return;

    const comment = comments.find((c) => c.id === commentId);
    if (!comment) return;

    if (comment.text === editedText) {
      setIsEditing(null);
      setEditedText("");
      return;
    }

    try {
      // Inject structured tokens using the local mentionMap
      const injectedText = injectStructuredMentions(editedText.trim(), mentionMap);

      const convertedText = await convertLegacyMentions(injectedText);
      const validatedMentions = validateStructuredMentions(convertedText);
      const safeText = validatedMentions.text.trim();
      
      if (!safeText) {
        toast.error("Comment cannot be empty");
        return;
      }
      if (validatedMentions.hadInvalidTokens) {
        toast.warn("Some malformed mentions were normalized during edit.");
      }

      await dispatch(
        updateComment({
          commentId,
          text: safeText,
          postId,
        }),
      ).unwrap();

      Promise.resolve()
        .then(async () => {
          const [oldUserIds, newUserIds] = await Promise.all([
            getMentionedUserIds(comment.text),
            getMentionedUserIds(safeText),
          ]);
          const newMentions = [...newUserIds].filter(
            (id) => !oldUserIds.has(id),
          );
          if (newMentions.length === 0) return;
          const currentUserId = currentUser?.uid || auth?.currentUser?.uid;
          const filteredIds = newMentions.filter((id) => id !== currentUserId);
          if (filteredIds.length > 0) {
            await triggerMentionNotification(filteredIds, {
              postId,
              commentId,
              type: "comment",
              text: safeText.substring(0, 100),
            });
          }
        })
        .catch((mentionError) => {
          console.warn(
            "Failed to send mention notifications on edit:",
            mentionError,
          );
        });

      toast.success("Comment updated successfully");
      setIsEditing(null);
      setEditedText("");
      setMentionMap({});
    } catch (error) {
      console.error("Error updating comment:", error);
      toast.error(error.message || "Failed to update comment");
    }
  };

  const handleCommentLike = async (commentId, comment) => {
    if (!currentUser) {
      toast.error("Please log in to like comments");
      return;
    }

    const alreadyLiked = !!likedCommentIds[commentId];
    const prevLikes = typeof comment?.likes === "number" ? comment.likes : 0;
    dispatch(
      patchCommentLikeCount({
        postId,
        commentId,
        likes: alreadyLiked ? Math.max(0, prevLikes - 1) : prevLikes + 1,
      }),
    );

    try {
      await dispatch(toggleCommentLikeAsync({ postId, commentId })).unwrap();
    } catch {
      dispatch(
        patchCommentLikeCount({ postId, commentId, likes: prevLikes }),
      );
      return;
    }

    if (
      !alreadyLiked &&
      !comment?.deletedAt &&
      comment?.userId &&
      comment.userId !== currentUser.uid
    ) {
      try {
        const { default: notificationService } =
          await import("../../../../../features/notifications/services/notificationService");
        await notificationService.createNotification({
          userId: comment.userId,
          actorId: currentUser.uid,
          type: "like",
          postId: postId,
          commentId: commentId,
          targetType: "comment",
          contentPreview: comment.text?.substring(0, 50),
        });
      } catch (_) {}
    }
  };

  const toggleReplies = (commentId, commentUser = null) => {
    const newState = showReplyInput === commentId ? null : commentId;
    setShowReplyInput(newState);
    setReplyToUser(newState ? commentUser : null);

    if (newState) {
      try {
        dispatch(hydrateRepliesFromCache(commentId));
      } catch (err) {}

      const now = Date.now();
      const lastFetchedAt = replyFetchTimestamps.current.get(commentId) || 0;
      const REPLY_TTL_MS = 30000;
      const isFresh = now - lastFetchedAt < REPLY_TTL_MS;

      if (!isFresh) {
        replyFetchTimestamps.current.set(commentId, now);
        dispatch(fetchCommentReplies(commentId));
      }
    }
  };

  const handleDeleteClick = (commentId) => {
    dispatch(
      openDeleteConfirm({
        itemId: commentId,
        itemType: "comment",
      }),
    );
    setShowOptions(null);
  };

  return {
    state: {
      isLoading,
      commentText,
      sortOrder,
      isEditing,
      editedText,
      showOptions,
      isExpanded,
      isSubmitting,
      showReplyInput,
      replyToUser,
      mentionMap,
      COMMENT_MAX_LENGTH,
      INITIAL_DISPLAY_COUNT,
    },
    derived: {
      totalComments,
      remainingComments,
      hasMoreOnServer,
      displayCount,
      visibleComments,
      displayedComments,
      deleteConfirmState,
      savedSet,
      currentUser,
      commentLikePending,
      likedCommentIds,
    },
    refs: {
      commentInputRef,
      optionsMenuRef,
      commentSectionRef,
    },
    handlers: {
      setCommentText,
      setSortOrder: handleSortChange,
      setIsEditing,
      setEditedText,
      setShowOptions,
      handleLoadMoreComments,
      handleShowLessComments,
      handleToggleSaveComment,
      handleCommentSubmit,
      handleCommentEdit,
      handleCommentLike,
      toggleReplies,
      handleDeleteClick,
      handleMentionSelect,
      isCommentOwner,
    },
  };
};
